// import moment from 'moment';
import _ from 'lodash';
import area from '@turf/area';

import { createAction } from 'redux-actions';
import { createSelector } from 'reselect';

import SwaggerAPI from 'data/SwaggerAPI';
import Organizations from './Organizations';
import { DashboardHelpers } from 'utils';

const SET_SELECTED_PROPERTY = 'properties/SET_SELECTED_PROPERTY';
const REQUEST_PROPERTIES = 'properties/REQUEST_PROPERTIES';
const UPDATE_PROPERTY = 'properties/UPDATE_PROPERTY';
const RECEIVE_PROPERTIES = 'properties/RECEIVE_PROPERTIES';
const RECEIVE_USER_PROPERTIES = 'properties/RECEIVE_USER_PROPERTIES';
const CREATE_PROPERTY = 'properties/CREATE_PROPERTY';
const DELETE_PROPERTY = 'properties/DELETE_PROPERTY';
const DELETE_USER_PROPERTY = 'properties/DELETE_USER_PROPERTY';
const CREATE_USER_PROPERTY = 'properties/CREATE_USER_PROPERTY';

const initialState = {
  isFetchingProperties: false,
  selectedProperty: '', // 'b7e58cc6-b6ea-41e0-a7a8-7c5703224bfb',
  properties: [],
  userProperties: [],
  initialized: false,
  orgStart: 0,
  orgEnd: 1,
  pageSize: 100,
  error: undefined,
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_SELECTED_PROPERTY: {
      if (action.propertyId === state.selectedProperty) return state;

      return {
        ...state,
        selectedProperty: action.propertyId ? action.propertyId : initialState.selectedProperty,
      };
    }
    case UPDATE_PROPERTY: {
      const props = [...state.properties];
      const index = _.findIndex(props, { id: action.payload.id });
      if (index !== -1) {
        props[index] = action.payload;
      }
      return {
        ...state,
        properties: props,
      };
    }

    case CREATE_PROPERTY: {
      if (action.error) {
        return state;
      }

      const props = [...state.properties];

      props.push(action.payload);

      return {
        ...state,
        properties: props,
        selectedProperty: action.payload.id,
      };
    }

    case DELETE_PROPERTY: {
      const propertiesCopy = [...state.properties];
      _.remove(propertiesCopy, (property) => property.id === state.selectedProperty);
      return {
        ...state,
        selectedProperty: '',
        properties: propertiesCopy,
      };
    }
    case REQUEST_PROPERTIES: {
      return { ...state, initialized: true, isFetchingProperties: true };
    }
    case RECEIVE_PROPERTIES: {
      if (!action.payload) return state;
      if (action.error) {
        return {
          ...state,
          isFetchingProperties: false,
          error: action.payload,
        };
      }

      const propCopy = _.unionWith(
        state.properties,
        action.payload,
        (property, newProperty) => property.id === newProperty.id
      );

      return {
        ...state,
        isFetchingProperties: false,
        properties: _.sortBy(propCopy, ['name']),
        error: undefined,
      };
    }

    case RECEIVE_USER_PROPERTIES: {
      if (!action.payload) return state;
      if (action.error) {
        return {
          ...state,
          isFetchingProperties: false,
          error: action.payload,
        };
      }

      return {
        ...state,
        isFetchingProperties: false,
        userProperties: action.payload,
        error: undefined,
      };
    }

    default:
      return state;
  }
};

const Properties = (function () {
  const setSelectedProperty = (propertyId) => ({ type: SET_SELECTED_PROPERTY, propertyId });

  const startFetch = () => ({ type: REQUEST_PROPERTIES });
  const fetchProperties = createAction(RECEIVE_PROPERTIES, SwaggerAPI.propertyList); // TeralyticAPI.getProperties);
  const fetchUserProperties = createAction(RECEIVE_USER_PROPERTIES, SwaggerAPI.propertyList); // TeralyticAPI.getProperties);
  const createProperty = createAction(CREATE_PROPERTY, SwaggerAPI.propertyCreate);
  const updateProperty = createAction(UPDATE_PROPERTY, SwaggerAPI.propertyUpdate);
  const deleteProperty = createAction(DELETE_PROPERTY, SwaggerAPI.propertyDelete);
  const deleteUserProperty = createAction(DELETE_USER_PROPERTY, SwaggerAPI.userPropertyDelete);
  const createUserProperty = createAction(CREATE_USER_PROPERTY, SwaggerAPI.userPropertyCreate);

  const getInitialized = (state) => state.properties.initialized;
  const getIsFetching = (state) => state.properties.isFetchingProperties;
  const getIsFetchingProperties = (state) => state.properties.isFetchingProperties;
  const getActivePropertyId = (state) => state.properties.selectedProperty;

  const getProperties = (state) => state.properties.properties;
  const getUserProperties = (state) => state.properties.userProperties;
  const getError = (state) => state.properties.error;
  const getPropertiesPage = (state) => ({
    properties: state.properties.properties,
    start: state.properties.start,
    end: state.properties.end,
  });

  const getOrgProperties = createSelector(
    [getProperties, Organizations.getActiveOrganizationId],
    (properties, orgId) => {
      return _.filter(properties, { organization_id: orgId });
    }
  );

  const getActiveProperty = createSelector([getProperties, getActivePropertyId], (properties, id) => {
    const prop = _.find(properties, { id });
    return prop;
  });

  const getActivePropertyFields = createSelector([getActiveProperty], (selectedProperty) => {
    const fields = [];
    if (selectedProperty) {
      _.forEach(selectedProperty.fields, (f) => {
        f.area = (area(f.geometry) / 10000).toFixed(2);
        fields.push(f);
      });
    }
    return fields;
  });

  const getPropertyBounds = createSelector([getActivePropertyFields], (fields) => {
    return DashboardHelpers.getBounds(fields);
  });

  return {
    startFetch,
    setSelectedProperty,
    fetchProperties,
    fetchUserProperties,
    createProperty,
    updateProperty,
    deleteProperty,
    deleteUserProperty,
    createUserProperty,

    getIsFetching,
    getIsFetchingProperties,
    getActiveProperty,
    getActivePropertyFields,
    getActivePropertyId,
    getProperties,
    getUserProperties,
    getPropertiesPage,
    getPropertyBounds,
    getInitialized,
    getOrgProperties,
    getError,
  };
})();

export default Properties;
